import {messages} from '@smartmixin/i18n';
import type {IntlShape} from 'react-intl';
import {POINT_NOW_MAX_ITEMS} from '../../api/actions/weather';
import type {
  Location,
  PointNowItem,
  PointNowRequestItem,
} from '../../api/types';
import {formatDate, hourFormatter} from '../Dates';
import type {DateTimeConfig} from '../Dates';
import {SupportedForecastModelList} from '../Forecast/constants';
import type {NormalizedForecastModel} from '../Forecast/types';
import {formatForecastModelTitle} from '../Forecast/title';
import * as Units from '../Units';

/**
 * The My weather list's place readings (FE #381): the current hour of every
 * place row from one `POST forecast/point/now/` (backend #267), and what a
 * row shows from it. Platform-free: each app draws the values.
 */

type FormatMessage = IntlShape['formatMessage'];

/** What a row needs of a place to ask for its hour. */
export type NowPlace = Pick<Location, 'hash' | 'coordinates' | 'timezone'>;

export type NowRequestItem = {
  /** Identifies the item: places asking the same thing share it. */
  key: string;
  item: PointNowRequestItem;
};

export type NowRequest = {
  /** Each place's item key, by place hash. */
  keyByHash: Record<string, string>;
  /** The distinct items, in the order the places came. */
  items: NowRequestItem[];
};

/**
 * Session cache lifetime. The server serves one row per hour and caches it
 * until the hour ends, so a fresher answer only differs when a new run
 * lands: ten minutes keeps a list opened and closed all day well inside the
 * endpoint's budget (30/min, 300/hour) and its values at most one run late.
 */
export const NOW_TTL_MS = 10 * 60 * 1000;

const HOUR_MS = 3600 * 1000;

/**
 * Whether a cached answer can stand in for a call: younger than the TTL and
 * from the same hour. The hour is UTC: the rows are on whole UTC hours, so
 * a place at UTC+5:30 still changes row at the UTC hour.
 */
export const isNowFresh = (fetchedAt: number, now: number): boolean =>
  now - fetchedAt < NOW_TTL_MS &&
  Math.floor(fetchedAt / HOUR_MS) === Math.floor(now / HOUR_MS);

/**
 * Below this a new fix is the same place: closer than the finest model grid
 * the list serves for free (GFS, 25 km) by far, and than any town.
 */
const SAME_PLACE_KM = 2;
const EARTH_RADIUS_KM = 6371;

type Coordinates = Location['coordinates'];

/**
 * Whether two positions are the same place for the list (FE #381 decision
 * 3): a re-located Current location keeps its readings until a fix lands
 * somewhere else.
 */
export const isSamePlace = (a: Coordinates, b: Coordinates): boolean => {
  const rad = Math.PI / 180;
  // Equirectangular: exact enough at a few km, and cheap.
  const x =
    (b.longitude - a.longitude) *
    rad *
    Math.cos(((a.latitude + b.latitude) / 2) * rad);
  const y = (b.latitude - a.latitude) * rad;
  return Math.sqrt(x * x + y * y) * EARTH_RADIUS_KM < SAME_PLACE_KM;
};

/** A model as the server names it: what `point/now` is asked for. */
export type NowModel = NormalizedForecastModel['serverId'];

/**
 * The item for a place, asking for `model`: the one its forecast screen
 * resolves (`getSelectedForecastModelByLocationHash`), so the row and the
 * screen it opens agree (#378 rule 3). None resolved (the place's model
 * list not known yet): nothing, and the server serves its default, GFS.
 */
const nowRequestItem = (
  place: NowPlace,
  model: NowModel | null | undefined,
): NowRequestItem => {
  const {latitude, longitude, altitude} = place.coordinates;
  // GFS on its meshless entry is what the server serves when asked for
  // nothing: one item, one key, either way (a list landing for a free user
  // must not ask again).
  const asked =
    model && !(model.forecastModel === 'GFS' && model.meshSize === null)
      ? model
      : null;
  const item: PointNowRequestItem = {
    lat: latitude,
    lon: longitude,
    ...(typeof altitude === 'number' ? {elevation: altitude} : {}),
    ...(place.timezone ? {timezone: place.timezone} : {}),
    ...(asked
      ? {forecast_model: asked.forecastModel, mesh_size: asked.meshSize}
      : {}),
  };
  const key = [
    item.lat,
    item.lon,
    item.elevation ?? '',
    item.timezone ?? '',
    item.forecast_model ?? '',
    item.mesh_size ?? '',
  ].join('|');
  return {key, item};
};

/**
 * One item per distinct place-and-model, for every place given, each
 * asking for its model by place hash. The model is part of the item's key,
 * so a place whose resolved model changes is asked again.
 */
export const buildNowRequest = (
  places: NowPlace[],
  modelByHash: Record<string, NowModel | null | undefined>,
): NowRequest => {
  const keyByHash: Record<string, string> = {};
  const items = new Map<string, NowRequestItem>();
  for (const place of places) {
    const entry = nowRequestItem(place, modelByHash[place.hash]);
    keyByHash[place.hash] = entry.key;
    if (!items.has(entry.key)) {
      items.set(entry.key, entry);
    }
  }
  return {keyByHash, items: [...items.values()]};
};

/** The items in requests of at most the server cap (ceil(n / 20) calls). */
export const chunkNowItems = <T>(items: T[]): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += POINT_NOW_MAX_ITEMS) {
    chunks.push(items.slice(i, i + POINT_NOW_MAX_ITEMS));
  }
  return chunks;
};

/** Why a row has nothing to show: the forecast, or the phone's network. */
export type NowErrorReason = 'forecast' | 'offline';

/** One place's readings, as the list holds them. */
export type NowState =
  | {status: 'loading'}
  | {status: 'data'; item: PointNowItem}
  | {status: 'error'; reason: NowErrorReason};

export type NowWind = {
  /** The mean wind, never the gust (BE #270). */
  speed: string;
  unit: string;
  /** Degrees, for the arrow; null when the row has none. */
  direction: number | null;
  /** The compass point, null when calm or unknown. */
  compass: string | null;
  calm: boolean;
};

export type NowRain = {
  /** mm in the row's interval, for the drop. */
  mm: number;
  wet: boolean;
  /**
   * The amount, shown only while it rains. An amount, never a rate: RR is
   * what falls in the row's interval, 1 h for an hourly model but 3 or 6 h
   * for ECMWF and GFS's tail (backend rain_postprocessor, stored as RR_1H),
   * so it reads as the forecast screen's does (mm, no "/h").
   */
  amount: string | null;
  unit: string;
};

export type NowTemperature = {value: string; unit: string};

export type PlaceNowView =
  | {kind: 'loading'}
  | {kind: 'error'; reason: NowErrorReason; message: string}
  | {
      kind: 'data';
      /** Null when the row lacks the value: the slot stays empty. */
      wind: NowWind | null;
      rain: NowRain | null;
      temperature: NowTemperature | null;
      /** The sky (the backend's icon code). */
      icon: number | null;
      /** "Calculated for 18:00 · GFS", or null without a time or model. */
      footer: string | null;
    };

export type PlaceNowContext = {
  units: Units.Units;
  dateTimeConfig: DateTimeConfig;
  /** The place's, for the time. */
  timezone: string;
  formatMessage: FormatMessage;
};

/**
 * The display name of the model served (`forecast_model` + `mesh_size`):
 * its catalogue title, the mesh-specific entry first (ARPEGE Europe and
 * Global share the name), then the meshless one. A model the catalogue does
 * not know keeps its server name.
 */
export const nowModelTitle = (
  forecastModel: string | null,
  meshSize: string | null,
  formatMessage: FormatMessage,
): string | null => {
  if (!forecastModel) {
    return null;
  }
  const named = SupportedForecastModelList.filter(
    (entry) => entry.serverId.forecastModel === forecastModel,
  );
  const entry =
    named.find((e) => e.serverId.meshSize === meshSize) ??
    named.find((e) => e.serverId.meshSize === null) ??
    named[0];
  return entry
    ? formatForecastModelTitle(entry.title, formatMessage)
    : forecastModel;
};

const ERROR_MESSAGES = {
  forecast: messages.forecastModels.forecastLoadError,
  offline: messages.network.networkError,
} as const;

const errorView = (
  reason: NowErrorReason,
  formatMessage: FormatMessage,
): PlaceNowView => ({
  kind: 'error',
  reason,
  message: formatMessage(ERROR_MESSAGES[reason]),
});

const windView = (item: PointNowItem, units: Units.Units): NowWind | null => {
  if (item.FF === null) {
    return null;
  }
  const calm = item.FF === 0;
  const point = calm ? null : Units.getPolarAngleFromDegrees(item.DD);
  return {
    speed: Units.convertValueToString(item.FF, 'Wind', units),
    unit: Units.getWindUnitLabel(units),
    direction: item.DD,
    compass: point,
    calm,
  };
};

const rainView = (item: PointNowItem, units: Units.Units): NowRain | null => {
  if (item.RR === null) {
    return null;
  }
  const wet = item.RR > 0;
  return {
    mm: item.RR,
    wet,
    amount: wet ? Units.convertValueToString(item.RR, 'Rain', units) : null,
    unit: Units.getRainUnitLabel(units),
  };
};

/** What a place row shows for its state. */
export const placeNowView = (
  state: NowState,
  {units, dateTimeConfig, timezone, formatMessage}: PlaceNowContext,
): PlaceNowView => {
  if (state.status === 'loading') {
    return {kind: 'loading'};
  }
  if (state.status === 'error') {
    return errorView(state.reason, formatMessage);
  }
  const {item} = state;
  if (item.error !== null || item.timestamp === null) {
    return errorView('forecast', formatMessage);
  }
  const wind = windView(item, units);
  const model = nowModelTitle(
    item.forecast_model,
    item.mesh_size,
    formatMessage,
  );
  return {
    kind: 'data',
    wind: wind && {
      ...wind,
      compass: wind.compass
        ? formatMessage(messages.compass[wind.compass])
        : null,
    },
    rain: rainView(item, units),
    temperature:
      item.T === null
        ? null
        : {
            value: Units.convertValueToString(item.T, 'Temperature', units),
            unit: Units.getTemperatureUnitLabel(units),
          },
    icon: item.ICON,
    footer: model
      ? formatMessage(messages.stations.calculatedFor, {
          time: formatDate(
            item.timestamp,
            timezone,
            hourFormatter(dateTimeConfig),
          ),
          model,
        })
      : null,
  };
};
